import { defineStore } from 'pinia';
import {
    agentCommissionConfigListType,
    AgentCommissionType,
    AgentPreferentialActivityInfoType,
    AgentPreferentialActivityType,
    AgentTopThreeType,
    AppConfigType,
    DetailsAgentListType,
    helpDetailType,
    helpListType,
    LimitDataType,
    MyBonusType,
    SystemDataType,
    TopAllAgentListType,
    ActivityPageType,
    ActivityGameTaskType,
    ActivityGameTaskInfoType,
    ActivityDetailType,
} from 'types/appConfig';
import agent from 'service/agent';

interface AppState {
    commissionWeb: AgentCommissionType | {};
    topThree: AgentTopThreeType[];
    preferentialActivity: AgentPreferentialActivityType[];
    preferentialActivityInfo: AgentPreferentialActivityInfoType | {};
    agentCommissionConfigList: agentCommissionConfigListType | {};
    topAllAgentList: TopAllAgentListType[];
    myBonus: MyBonusType | {};
    bonusMoney: number;
    helpList: helpListType[];
    helpDetail: helpDetailType | {};
    activityList: ActivityPageType[];
    activityGameTask: ActivityGameTaskType[];
    activityGameTaskInfo: ActivityGameTaskInfoType | {};
    activityDetail: ActivityDetailType | {};
    activityLoading: boolean;
}

export const useAgentStore = defineStore('agent', {
    state: (): AppState => {
        return {
            commissionWeb: {},
            topThree: [],
            preferentialActivity: [],
            preferentialActivityInfo: {},
            agentCommissionConfigList: {},
            topAllAgentList: [],
            myBonus: {},
            bonusMoney: 0,
            helpList: [],
            helpDetail: {},
            activityList: [],
            activityGameTask: [],
            activityGameTaskInfo: {},
            activityDetail: {},
            activityLoading: true,
        };
    },
    getters: {
        GetCommissionWeb: (state: AppState) => state.commissionWeb,
        GetTopThree: (state: AppState) => state.topThree,
        GetPreferentialActivity: (state: AppState) => state.preferentialActivity,
        GetPreferentialActivityInfo: (state: AppState) => state.preferentialActivityInfo,
        GetAgentCommissionConfigList: (state: AppState) => state.agentCommissionConfigList,
        GetTopAllAgentList: (state: AppState) => state.topAllAgentList,
        GetMyBonus: (state: AppState) => state.myBonus,
        GetBonusMoney: (state: AppState) => state.bonusMoney,
        HelpList: (state: AppState) => state.helpList,
        HelpDetail: (state: AppState) => state.helpDetail,
        ActivityList: (state: AppState) => state.activityList,
        ActivityGameTask: (state: AppState) => state.activityGameTask,
        ActivityGameTaskInfo: (state: AppState) => state.activityGameTaskInfo,
        ActivityDetail: (state: AppState) => state.activityDetail,
        ActivityLoading: (state: AppState) => state.activityLoading,
    },
    actions: {
        // 优惠活动弹窗状态还原
        clearLoading() {
            this.activityLoading = false;
        },
        // 推广赚钱
        async handleGetReturnCommissionWeb() {
            try {
                const result = await agent.getReturnCommissionWeb();
                if (result && result.code === 0) {
                    this.commissionWeb = result.data;
                }
                return result;
            } catch (e) {
                await Promise.reject(e);
            }
        },
        // 代理排行榜前三
        async handleGetTopThree() {
            try {
                const result = await agent.getTopThree();
                if (result && result.code === 0) {
                    this.topThree = result.data;
                }
                return result;
            } catch (e) {
                await Promise.reject(e);
            }
        },
        // 代理返佣配置
        async handleGetAgentCommissionConfigList() {
            try {
                const result = await agent.getAgentCommissionConfigList();
                if (result && result.code === 0) {
                    this.agentCommissionConfigList = result.data;
                }
                return result;
            } catch (e) {
                await Promise.reject(e);
            }
        },
        // 代理成就规则
        async handleGetPreferentialActivity(data) {
            try {
                const result = await agent.getPreferentialActivity(data);
                if (result && result.code === 0) {
                    if (data?.isGetCount === 0) {
                        this.preferentialActivity = result?.data?.list || [];
                    } else {
                        this.preferentialActivity = result?.data?.list || [];
                        this.preferentialActivityInfo = result?.data?.info;
                    }
                }
                return result;
            } catch (e) {
                await Promise.reject(e);
            }
        },
        // 优惠活动任务
        async handleGetFindGameTaskRules(data) {
            try {
                const result = await agent.findGameTaskRules(data);
                if (result && result.code === 0) {
                    this.activityGameTask = result?.data?.list || [];
                    this.activityGameTaskInfo = result?.data?.info;
                }
                return result;
            } catch (e) {
                await Promise.reject(e);
            }
        },
        // 优惠活动任务
        async handleGetFindCompleteInfoRules(data) {
            try {
                const result = await agent.findCompleteInfoRules(data);
                if (result && result.code === 0) {
                    this.activityGameTask = result?.data?.list || [];
                    this.activityGameTaskInfo = result?.data?.info;
                }
                return result;
            } catch (e) {
                await Promise.reject(e);
            }
        },
        // 优惠活动详情
        async handleGetFindEnabledById(data) {
            try {
                this.activityLoading = true;
                const result = await agent.findById(data);
                if (result?.code === 0) {
                    this.activityDetail = result?.data || {};
                    this.activityLoading = false;
                }
                return result;
            } catch (e) {
                await Promise.reject(e);
            } finally {
            }
        },
        // 代理活动申请
        async handleGetPreferentialActivityApply(data) {
            try {
                return await agent.getPreferentialActivityApply(data);
            } catch (e) {
                await Promise.reject(e);
            }
        },
        // 代理奖金列表
        async handleGetTopAllAgent(data) {
            try {
                const res = await agent.getTopAllAgent(data);
                if (res && res?.code === 0) {
                    this.topAllAgentList = res?.data;
                }
                return res;
            } catch (e) {
                await Promise.reject(e);
            }
        },
        // 代理奖金列表
        async handleGetMyBonus() {
            try {
                const res = await agent.getMyBonus();
                if (res && res?.code === 0) {
                    this.myBonus = res?.data;
                }
                return res;
            } catch (e) {
                await Promise.reject(e);
            }
        },
        // 代理邀请奖励
        async handleGetAgentInvitationBonus() {
            try {
                const res = await agent.getAgentInvitationBonus();
                if (res && res?.code === 0) {
                    this.bonusMoney = res?.data;
                }
                return res;
            } catch (e) {
                await Promise.reject(e);
            }
        },
        // 充值返利
        async handleGetFindAgent(data) {
            try {
                return await agent.getFindAgent(data);
            } catch (e) {
                await Promise.reject(e);
            }
        },
        // 代理总计
        async handleGetFindAgentTotal(data) {
            try {
                return await agent.getFindAgentTotal(data);
            } catch (e) {
                await Promise.reject(e);
            }
        },
        // 投注回扣
        async handleGetAgentCommissionRecord(data) {
            try {
                return await agent.getAgentCommissionRecord(data);
            } catch (e) {
                await Promise.reject(e);
            }
        },
        // 帮助中心列表
        async handleGetFindHomeHelp() {
            try {
                const res = await agent.getFindHomeHelp();
                if (res && res?.code === 0) {
                    this.helpList = res?.data;
                }
                return res;
            } catch (e) {
                await Promise.reject(e);
            }
        },
        // 帮助中心详情
        async handleGetHomeHelpFindById(data) {
            try {
                const res = await agent.getHomeHelpFindById(data);
                if (res && res?.code === 0) {
                    this.helpDetail = res?.data;
                }
                return res;
            } catch (e) {
                await Promise.reject(e);
            }
        },
        // 帮助中心详情
        async handleGetFindFAQ() {
            try {
                const res = await agent.getFindFAQ();
                // if (res && res?.code === 0) {
                //     this.helpDetail = res?.data;
                // }
                return res;
            } catch (e) {
                await Promise.reject(e);
            }
        },
        // H5 书签
        async handleGetFindBookmark(data) {
            try {
                const res = await agent.getFindBookmark(data);
                return res;
            } catch (e) {
                await Promise.reject(e);
            }
        },
        // 领取佣金
        async handleGetCommissionReceive(data) {
            try {
                return await agent.getCommissionReceive(data);
            } catch (e) {
                await Promise.reject(e);
            }
        },

        // 优惠活动
        async handleGetFindEnabledPage() {
            try {
                const res = await agent.findPage();
                if (res?.code === 0) {
                    this.activityList = res?.data;
                }
                return res;
            } catch (e) {
                await Promise.reject(e);
            }
        },
        // 代理层级
        async handleGetAgentLevelSum(data) {
            try {
                return await agent.getAgentLevelSum(data);
            } catch (e) {
                await Promise.reject(e);
            }
        },
        // 超级大奖
        async handleGetFindGameBetPreferential() {
            try {
                return await agent.findGameBetPreferential();
            } catch (e) {
                await Promise.reject(e);
            }
        },
        // 超级大奖详情列表
        async handleGetFindBetBonus(data) {
            try {
                return await agent.findBetBonus(data);
            } catch (e) {
                await Promise.reject(e);
            }
        },
        // 获取冠军活动
        async handleGetFindChampionship(data) {
            try {
                return await agent.findChampionship(data);
            } catch (error) {
                await Promise.reject(error);
            }
        },
        // 获取活动记录
        async handleGetPreferentialFlow(data) {
            try {
                return await agent.getPreferentialFlow(data);
            } catch (error) {
                await Promise.reject(error);
            }
        },

        // 代理返佣规则
        async getCommissionConfig() {
            try {
                return await agent.getCommissionConfig();
            } catch (error) {
                await Promise.reject(error);
            }
        },

        // 代理升星规则
        async getAgentCommissionStarConfig() {
            try {
                return await agent.getAgentCommissionStarConfig();
            } catch (error) {
                await Promise.reject(error);
            }
        },
    },
});
